import { Role } from 'consts/auth';
import { LoadingPage, SignUpPage } from 'pages';
import { AuthCallback } from 'pages/AuthCallback/AuthCallback.page';
import React from 'react';
import { Redirect, Route, Switch } from 'react-router-dom';
import { PrivateRoute } from 'routes/PrivateRoute';

const Dashboard = React.lazy(() => import('components/dashboard').then(m => ({ default: m.Dashboard })));
const DashboardPage = React.lazy(() => import('pages/Dashboard/Dashboard.page'));
const HomePage = React.lazy(() => import('pages/Home/Home.page'));
const DailyReportsPage = React.lazy(() => import('pages/DailyReport/DailyReports.page'));
const DailyReportPage = React.lazy(() => import('pages/DailyReport/DailyReport.page'));
const ChatPage = React.lazy(() => import('pages/Chat').then(m => ({ default: m.ChatPage })));
const ScheduledTasksPage = React.lazy(() => import('pages/Chat/ScheduledTasks.page').then(m => ({ default: m.ScheduledTasksPage })));
const CustomizePage = React.lazy(() => import('pages/Chat/Customize.page').then(m => ({ default: m.CustomizePage })));
const ShiptrackingPage = React.lazy(() => import('pages/ShiptrackingPage'));
const CoTPage = React.lazy(() => import('pages/CoTPage'));
const CoTTestPage = React.lazy(() => import('pages/CoTTestPage'));
const FundamentalsSummaryPage = React.lazy(() => import('pages/FundamentalsSummaryPage'));
const GeopoliticsPage = React.lazy(() => import('pages/GeopoliticsPage'));
const GlobalFlowsPage = React.lazy(() => import('pages/GlobalFlowsPage'));
const GlobalFlowsTestPage = React.lazy(() => import('pages/GlobalFlowsTestPage'));
const OpenInterestPage = React.lazy(() => import('pages/OpenInterestPage'));
const GlobalMarginsPage = React.lazy(() => import('pages/GlobalMarginsPage'));
const OpenInterestTestPage = React.lazy(() => import('pages/OpenInterestTestPage'));
const GenUICatalogPage = React.lazy(() => import('pages/GenUICatalogPage'));
const DebugToolResponsesPage = React.lazy(() => import('pages/DebugToolResponses/DebugToolResponses.page'));
const TankerDataHubPage = React.lazy(() => import('pages/TankerDataHub/TankerDataHub.page'));
const DataApiDocsPage = React.lazy(() => import('pages/DataApiDocs/DataApiDocs.page'));

export const Routes = () => {
  let crashRoute = null;

  if (process.env.NODE_ENV === 'development') {
    crashRoute = (
      <Route exact path="/crash" render={() => {
        throw new Error('Crash route triggered');
      }} />
    );
  }

  return (
    <React.Suspense fallback={<LoadingPage />}>
      <Switch>
        <PrivateRoute
          exact
          path={'/watchlists/:wlId/trades/:tradeId'}
          component={Dashboard}
          role={Role.FullAccess}
        />
        <PrivateRoute
          exact
          path={'/watchlists'}
          component={Dashboard}
          role={Role.FullAccess}
        />
        <PrivateRoute
          exact
          path={'/watchlists/:wlId'}
          component={Dashboard}
          role={Role.FullAccess}
        />
        <PrivateRoute
          exact
          path={'/dashboard/:id/:slug'}
          component={DashboardPage}
          role={Role.FullAccess}
        />
        <PrivateRoute
          exact
          path={'/dashboard/:id'}
          component={DashboardPage}
          role={Role.FullAccess}
        />
        <PrivateRoute
          exact
          path="/dashboard"
          component={HomePage}
          role={Role.FullAccess}
        />
        <PrivateRoute
          exact
          path="/fundamentals-summary"
          component={FundamentalsSummaryPage}
          role={Role.FullAccess}
        />
        <PrivateRoute
          exact
          path="/cot"
          component={CoTPage}
          role={Role.FullAccess}
        />
        <PrivateRoute
          exact
          path="/cot-test"
          component={CoTTestPage}
          role={Role.FullAccess}
        />
        <PrivateRoute
          exact
          path="/open-interest"
          component={OpenInterestPage}
          role={Role.FullAccess}
        />
        <PrivateRoute
          exact
          path="/open-interest-test"
          component={OpenInterestTestPage}
          role={Role.FullAccess}
        />
        <PrivateRoute
          exact
          path="/global-flows"
          component={GlobalFlowsPage}
          role={Role.FullAccess}
        />
        <PrivateRoute
          exact
          path="/global-flows-test"
          component={GlobalFlowsTestPage}
          role={Role.FullAccess}
        />
        <PrivateRoute
          exact
          path="/petrologistics-shiptracking"
          component={ShiptrackingPage}
        />
        <PrivateRoute
          exact
          path="/tanker-data-hub"
          component={TankerDataHubPage}
          role={Role.FullAccess}
        />
        <PrivateRoute
          exact
          path="/daily-reports"
          component={DailyReportsPage}
        />
        <PrivateRoute
          exact
          path={'/daily-reports/:id'}
          component={DailyReportPage}
        />
        <PrivateRoute
          exact
          path={'/chat/:id?'}
          component={ChatPage}
          role={Role.FullAccess}
        />
        <PrivateRoute
          exact
          path={'/scheduled-tasks'}
          component={ScheduledTasksPage}
          role={Role.FullAccess}
        />
        <PrivateRoute
          exact
          path={'/customize'}
          component={CustomizePage}
          role={Role.FullAccess}
        />
        <PrivateRoute
          exact
          path="/geopolitics"
          component={GeopoliticsPage}
          role={Role.FullAccess}
        />
        <PrivateRoute
          exact
          path="/global-margins"
          component={GlobalMarginsPage}
          role={Role.FullAccess}
        />
        <PrivateRoute
          exact
          path="/gen-ui-catalog"
          component={GenUICatalogPage}
        />
        <PrivateRoute
          exact
          path="/debug-tool-responses"
          component={DebugToolResponsesPage}
        />
        <PrivateRoute
          exact
          path="/data-api-docs"
          component={DataApiDocsPage}
        />
        <PrivateRoute
          exact
          path={'/'}
          component={Dashboard}
          role={Role.FullAccess}
        />
        <Route exact path={'/watchlist'} render={() => (<Redirect to="/watchlists" />)} />
        {crashRoute}
        {/*
          /print/artifact lives at the App.js level (outside PageLayoutContainer)
          so the headless renderer gets bare chrome. See App.js for the route.
        */}

        <Route exact path="/sign-up" render={(props) => <SignUpPage {...props} />} />
        <Route exact path="/callback" component={AuthCallback} />
      </Switch>
    </React.Suspense>
  );
};
